// Full-screen live preview for the Dashboard designer.
//
// Sends the current (unsaved) DashboardModel to /api/visual-data/dashboard,
// which resolves every tile's visual + criteria bindings and returns a dataset
// per tile. Tiles are placed on a 12-column grid by their POSITION and each is
// rendered with the shared <InsightVisual> host. A global-criteria bar lets the
// author change shared filters and re-run; one failing tile shows an inline
// error without breaking the rest.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { X, Loader2, Play, RefreshCw, Maximize2, Minimize2 } from 'lucide-react'

import toast from 'react-hot-toast'
import {
  InsightVisual, useAutoRefresh, UnderlyingDataModal,
  dashGridContainerStyle, dashTileStyle,
  QuickFilterBar, QuickFilterToggle, TileFilterBadge,
  chipsToFilterExpression, mergeQuickFilterPicks, removeQuickFilterValue,
  quickFilterKey, quickFilterReach, tileQuickFilterIndication,
  REASON_REPORT_TILE, REASON_TILE_ERROR,
  useVisualModels, MODEL_FALLBACK_REASON_TEXT,
  useDashboardAccent, DashboardHeader, DashboardFilterCard, DashboardQuickFilterBand,
  DashboardDrillTrail, DashboardTileFrame, DashboardTileMessage, DashboardDrillHint,
  DashboardIconButton, dashApplyButtonStyle, dashScreenPageStyle, useDashScreenMargin, useDashScreenShowsTitle,
  DASH_SCREEN_ROW_PX, DASH_SCREEN_GAP,
} from '@twasta/visual-engine'
import type {
  VisualDataPayload, UnderlyingPayload, QuickFilterChip, QuickFilterPick,
  TileQuickFilterStatus, FilterResult, VisualModel, VisualPlan,
} from '@twasta/visual-engine'
import {
  fetchDashboardData, fetchVisualData, makeVisualCriteriaRuntime,
  type DashboardDataResult,
} from '@/api/visualData'
import { loadDashboardObject } from '@/api/designers'
import PreviewLogMenu from '@/livepreview/runtime/PreviewLogMenu'
import {
  CriteriaBar, formatCriteriaValueForInput, dashboardCriteriaNeedInput, requiredIssues,
  type CriteriaIssue, type VisualCriteriaPromptVar,
} from '@twasta/page-runtime/txn'
import type { FilterExpression } from '@twasta/visual-engine'
import type { DashboardModel, DashboardDrillThrough, DashboardGlobalCriterion } from './types'
import { blankDashboardModel, sanitizeDashboardModel } from './helpers'
import { useProjectStore } from '@/store/projectStore'
import ReportEmbedBody from '../report/ReportEmbedBody'
import { tw } from '@twasta/page-runtime'
import { useShellOwnsFullScreen } from '@twasta/page-runtime/txn'

// Retains the last global-criteria values per dashboard across preview re-opens.
const GLOBAL_CACHE = new Map<string, Record<string, string>>()

// A model-backed tile carries no server dataset (its query was never run) — the
// engine renders from `model`/`plan` instead, so this only satisfies the prop.
const EMPTY_JSON_DATA = [{ id: 0 }] as unknown as NonNullable<
  DashboardDataResult['tiles'][number]['jsonData']
>

interface DashboardPreviewModalProps {
  open: boolean
  onClose: () => void
  projectName: string
  objectName: string
  /** The current in-memory model — previewed without a save (designer Preview
      button). Omit to load the SAVED model from disk (dashboard card "View"). */
  model?: DashboardModel
}

export default function DashboardPreviewModal({
  open, onClose, projectName, objectName, model,
}: DashboardPreviewModalProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', flexDirection: 'column' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        style={{
          flex: 1, margin: 16, borderRadius: 8, overflow: 'hidden',
          background: 'var(--color-bg)', display: 'flex', flexDirection: 'column',
          boxShadow: '0 12px 48px rgba(0,0,0,0.4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary)', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: '0.9125rem', fontWeight: 500, color: 'var(--color-text)' }}>
              Dashboard Preview
            </span>
            <span style={{ opacity: 0.5, fontSize: '0.8625rem' }}>—</span>
            <span style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {projectName} / {objectName}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <PreviewLogMenu projectName={projectName} objectName={objectName} />
            <button className="btn btn-ghost btn-sm" onClick={onClose} title="Close (Esc)" aria-label="Close">
              <X size={16} />
            </button>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: 'var(--color-bg)' }}>
          {/* In-memory model (designer Preview) renders directly; without one
              (card "View") the saved model is loaded from disk. Both paths feed
              the same PreviewBody, so there is one preview surface. */}
          {model ? (
            <PreviewBody key={`${projectName}/${objectName}`} project={projectName} model={model} objectName={objectName} cacheKey={`${projectName}/${objectName}`} />
          ) : (
            <ModalDiskPreview key={`disk:${projectName}/${objectName}`} project={projectName} objName={objectName} />
          )}
        </div>
      </div>
    </div>
  )
}

// One level of the drill-through navigation stack. The base level renders the
// inline (unsaved) dashboard model; drilled levels load a target dashboard OR
// a single target visual by obj name, with globals/params derived from the
// clicked row's PARAM_MAPPING. ('self' drills never push a level — they set
// the active level's globals instead.)
interface DrillLevel {
  kind: 'base' | 'drill'
  // What a drilled level renders. Absent (legacy) = 'dashboard'.
  target?: 'dashboard' | 'visual'
  objName?: string
  label: string
  globals: Record<string, string>
}

// Drop empty-string criteria before sending so a cleared/unset input falls
// back to the criterion's DEFAULT_VALUE on the server instead of binding '' into
// a tile's SQL (e.g. `BOOKING_DATE BETWEEN '' AND ''`, which raises an invalid
// input syntax for timestamp).
function pruneEmptyGlobals(globals: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(globals)) {
    if (v != null && String(v).trim() !== '') out[k] = v
  }
  return out
}

// Case-insensitive row field lookup (data keys are canonical NAME casing).
function lookupField(row: Record<string, unknown>, name: string): unknown {
  if (name in row) return row[name]
  const lk = name.toLowerCase()
  const hit = Object.keys(row).find((k) => k.toLowerCase() === lk)
  return hit ? row[hit] : undefined
}

// Loads the SAVED dashboard model from disk and returns it gated on the objName
// it was loaded FOR — otherwise, on an objName switch, one render passes the
// PREVIOUS dashboard's model with the NEW name to PreviewBody (stale
// inline_model run + wrong-tiles flash). Same guard as the visual loader.
// Shared by the workbench Live Preview pane and the dashboard card "View" modal.
export function useLoadedDashboardModel(project: string, objName: string) {
  const [loaded, setLoaded] = useState<{ model: DashboardModel; forObj: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    loadDashboardObject(project, objName)
      .then((data) => {
        if (cancelled) return
        const model = data && data.content
          ? sanitizeDashboardModel(data.content, objName)
          : blankDashboardModel(objName)
        setLoaded({ model, forObj: objName })
      })
      .catch(() => { if (!cancelled) setError('Failed to load dashboard model') })
    return () => { cancelled = true }
  }, [project, objName])

  return { model: loaded && loaded.forObj === objName ? loaded.model : null, error }
}

// Disk-loaded body for the dashboard card "View" modal — same PreviewBody the
// designer Preview and the navigation pane use, so there is one preview surface.
function ModalDiskPreview({ project, objName }: { project: string; objName: string }) {
  const { model, error } = useLoadedDashboardModel(project, objName)
  if (error) {
    return <div style={{ padding: 24, color: 'var(--color-text-secondary)', fontSize: 14 }}>{error}</div>
  }
  if (!model) {
    return (
      <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-secondary)', fontSize: 14 }}>
        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading preview…
      </div>
    )
  }
  return (
    <PreviewBody
      key={`${project}/${objName}`}
      project={project}
      model={model}
      objectName={objName}
      cacheKey={`${project}/${objName}`}
    />
  )
}

// Embeddable, non-modal live preview for the workbench "Live Preview" tab.
// Loads the saved dashboard model from disk and renders the same tile grid the
// modal uses.
export function DashboardLivePreviewPane({ project, objName, initialGlobals }: {
  project: string; objName: string
  /** Filter (GLOBAL_CRITERIA) values to open with — an 'open' action button
   *  on a transaction passing values from the row it was clicked on. They win
   *  over the values remembered from the last visit. */
  initialGlobals?: Record<string, unknown>
}) {
  const { model, error } = useLoadedDashboardModel(project, objName)
  const [fullscreen, setFullscreen] = useState(false)
  // Inside the Run shell its own full-screen button does this; a second one
  // here would duplicate it.
  const shellFullScreen = useShellOwnsFullScreen(objName, 'D')

  // Full screen must sit ABOVE the runtime shell's sticky top bar (z 9000) —
  // at a lower z-index the shell's header painted over the dashboard's own
  // criteria bar and hid it.
  const outerStyle: CSSProperties = fullscreen
    ? { position: 'fixed', inset: 0, zIndex: 9400, background: 'var(--color-bg)', display: 'flex', flexDirection: 'column' }
    : { height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--color-bg)' }

  // The toggle docks into the criteria bar's action row (beside Apply) rather
  // than owning a toolbar line above it.
  const fullscreenBtn = (
    <DashboardIconButton
      label={fullscreen ? 'Exit full screen' : 'Full screen'}
      onClick={() => setFullscreen((f) => !f)}
      pressed={fullscreen}
    >
      {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
    </DashboardIconButton>
  )

  return (
    <div style={outerStyle}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {error ? (
          <div style={{ padding: 24, color: 'var(--color-text-secondary)', fontSize: 14 }}>{error}</div>
        ) : !model ? (
          <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-text-secondary)', fontSize: 14 }}>
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading preview…
          </div>
        ) : (
          <PreviewBody
            key={`${project}/${objName}`}
            project={project}
            model={model}
            objectName={objName}
            cacheKey={`${project}/${objName}`}
            hostActions={shellFullScreen ? undefined : fullscreenBtn}
            initialGlobals={initialGlobals}
            overlay={fullscreen}
          />
        )}
      </div>
    </div>
  )
}

// The application's colour for a dashboard on THEME_COLOR 'default', when this
// pane is the open project's (the builder: workbench, designer preview). In the
// Run view the shell has already put it on the page (--app-theme-color), which
// useDashboardAccent reads when this is empty.
function useProjectAppColor(project: string): string | null {
  return useProjectStore((s) => (
    s.currentProject?.application.folder_name === project
      ? s.currentProject.application.theme_color || null
      : null
  ))
}

function PreviewBody({ project, model, objectName, cacheKey, hostActions, initialGlobals, overlay = false }: {
  project: string
  model: DashboardModel
  objectName: string
  cacheKey: string
  // Host chrome (e.g. the Run pane's full-screen toggle) docked into the
  // dashboard header's action row.
  hostActions?: ReactNode
  // Filter values to open with (see DashboardLivePreviewPane).
  initialGlobals?: Record<string, unknown>
  // In the pane's own full-screen overlay (a wider margin).
  overlay?: boolean
}) {
  // The margin round the screen — the transaction list's rule: none inside
  // the Run shell (its frame is the margin), a small one elsewhere.
  const screenMargin = useDashScreenMargin(objectName, 'D', overlay)
  const appColor = useProjectAppColor(project)
  // When the tiles were last fetched — the header's "Updated hh:mm".
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null)
  const [data, setData] = useState<DashboardDataResult | null>(null)
  // Dataset of a drilled-into SINGLE visual (TARGET_TYPE 'visual' levels).
  const [visualData, setVisualData] = useState<VisualDataPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  // The global-criteria bar opens COLLAPSED unless a required criterion has no
  // value (dashboardCriteriaNeedInput) — decided on every run that is not an
  // auto-refresh poll, so it also folds away after an Apply that satisfied it.
  const [filtersCollapsed, setFiltersCollapsed] = useState(true)
  // Findings of the criteria's checks — the server's (criteriaErrors /
  // criteriaNotices of the last run) or the bar's own required check.
  const [issues, setIssues] = useState<CriteriaIssue[]>([])
  // Monotonic run counter — drops out-of-order responses (rapid drill/back,
  // auto-refresh overlapping an Apply) so a stale fetch can't overwrite the
  // newest tiles/visual/error. See VisualPreviewModal for the same guard.
  const runSeq = useRef(0)
  // Underlying-data target: the client-side rows behind a double-clicked tile
  // point (or the tile's ⋮ menu), or null when the modal is closed.
  const [underlying, setUnderlying] = useState<UnderlyingPayload | null>(null)
  // ── Quick filter mode ──────────────────────────────────────────────────
  // A deliberate mode (off by default), then a click on any tile value filters
  // every tile. The chips are turned into ONE filter expression and handed to
  // each <InsightVisual>, which filters the data it has ALREADY loaded — so a
  // click is instant, no tile is re-queried, and the criteria bar above is
  // untouched. Each tile reports what it could evaluate, and a tile that has no
  // such column says so on itself instead of looking filtered.
  const [quickMode, setQuickMode] = useState(false)
  const [chips, setChips] = useState<QuickFilterChip[]>([])
  const [tileFilterResults, setTileFilterResults] = useState<Record<string, FilterResult>>({})
  const chipsKey = quickFilterKey(chips)
  const quickFilterExpr = useMemo(
    () => chipsToFilterExpression(chips),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chipsKey],
  )
  const applyPicks = useCallback(
    (picks: QuickFilterPick[], additive: boolean, sourceVisual?: string) => {
      if (!picks.length) {
        toast('Nothing to filter on there — that point carries no dimension value.')
        return
      }
      setChips((current) => mergeQuickFilterPicks(current, picks, { additive, sourceVisual }))
    },
    [],
  )
  // Escape leaves the mode (and is swallowed so it doesn't also close the
  // preview modal). Applied filters survive — leaving the mode stops new clicks
  // from filtering, it doesn't silently undo what you already applied.
  useEffect(() => {
    if (!quickMode) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      setQuickMode(false)
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [quickMode])

  const [levels, setLevels] = useState<DrillLevel[]>(() => {
    const passed: Record<string, string> = {}
    for (const [k, v] of Object.entries(initialGlobals || {})) {
      if (v !== undefined && v !== null && v !== '') passed[k] = String(v)
    }
    return [
      { kind: 'base', label: objectName || 'Dashboard', globals: { ...(GLOBAL_CACHE.get(cacheKey) || {}), ...passed } },
    ]
  })
  const activeIndex = levels.length - 1
  const active = levels[activeIndex]
  const isVisualLevel = active.kind === 'drill' && active.target === 'visual'

  // Persist only the base level's globals across re-opens.
  useEffect(() => {
    GLOBAL_CACHE.set(cacheKey, levels[0].globals)
  }, [cacheKey, levels])

  const setActiveGlobals = useCallback(
    (updater: (g: Record<string, string>) => Record<string, string>) => {
      setLevels((ls) => ls.map((l, i) => (i === ls.length - 1 ? { ...l, globals: updater(l.globals) } : l)))
    },
    [],
  )

  const run = useCallback(
    // silent = an auto-refresh poll: no loading flash, and a failed poll keeps
    // the last good tiles on screen instead of replacing them with an error.
    async (lvl: DrillLevel, applyGlobals: boolean, silent = false) => {
      const seq = ++runSeq.current
      if (!silent) { setLoading(true); setError(null) }
      try {
        const sentGlobals = pruneEmptyGlobals(lvl.globals)
        // A drilled-into single visual: fetch its dataset with the mapped
        // params from the clicked row and render it full-size instead of a
        // tile grid.
        if (lvl.kind === 'drill' && lvl.target === 'visual' && lvl.objName) {
          const payload = await fetchVisualData({
            project, obj_name: lvl.objName, params: sentGlobals,
          })
          if (seq !== runSeq.current) return
          setVisualData(payload)
          setData(null)
          setUpdatedAt(new Date())
          return
        }
        const payload =
          lvl.kind === 'drill' && lvl.objName
            ? await fetchDashboardData({
                project, obj_name: lvl.objName, global_criteria: sentGlobals, in_memory: true,
              })
            : await fetchDashboardData({
                project,
                obj_name: objectName,
                inline_model: model,
                global_criteria: applyGlobals || Object.keys(sentGlobals).length ? sentGlobals : undefined,
                in_memory: true,
              })
        if (seq !== runSeq.current) return
        setVisualData(null)
        setData(payload)
        setUpdatedAt(new Date())
        setIssues([...(payload.criteriaErrors || []), ...(payload.criteriaNotices || [])])
        if (!silent) {
          setFiltersCollapsed(!dashboardCriteriaNeedInput(
            payload.tiles, payload.dashboard.GLOBAL_CRITERIA))
        }
        // Seed any unset criteria from their server-RESOLVED value (token
        // defaults like :calendar_month_start resolved to a concrete date),
        // falling back to the raw DEFAULT_VALUE, coerced for the input (date
        // slice, unresolved :token → skipped). Leaving the rest unset (rather
        // than storing '') keeps empty inputs out of the echoed globals so they
        // never reach a tile's SQL.
        if (!applyGlobals) {
          const resolvedGlobals = payload.dashboard.resolvedGlobals || {}
          setLevels((ls) =>
            ls.map((l, i) => {
              if (i !== ls.length - 1) return l
              const next = { ...l.globals }
              for (const g of payload.dashboard.GLOBAL_CRITERIA || []) {
                if (next[g.PARAM_NAME] !== undefined) continue
                const resolved = resolvedGlobals[g.PARAM_NAME]
                const raw = resolved != null && resolved !== '' ? resolved : g.DEFAULT_VALUE
                const seed = formatCriteriaValueForInput(raw, g.DATA_TYPE)
                if (seed !== '') next[g.PARAM_NAME] = seed
              }
              return { ...l, globals: next }
            }),
          )
        }
      } catch (e) {
        if (seq !== runSeq.current) return
        if (!silent) setError(extractError(e))
      } finally {
        if (!silent && seq === runSeq.current) setLoading(false)
      }
    },
    [project, objectName, model],
  )

  // Fetch whenever the active level changes (open, drill, or back). Depends on
  // activeIndex + objName only, so editing globals doesn't re-fetch on keystroke.
  useEffect(() => {
    void run(active, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, active.objName, run])

  // Auto-refresh (the dashboard's REFRESH_INTERVAL_SECONDS): silently re-fetch
  // the active level's tiles with the currently applied globals.
  const refreshSeconds = Number(data?.dashboard?.REFRESH_INTERVAL_SECONDS) || 0
  useAutoRefresh(refreshSeconds, () => run(active, true, true))

  // Drill into a target dashboard when a drill-source tile's row is clicked.
  const drillThrough = (data?.dashboard.DRILL_THROUGH ?? []) as DashboardDrillThrough[]
  const drillSources = useMemo(
    () => new Set(drillThrough.map((d) => d.SOURCE_VISUAL_ID)),
    [drillThrough],
  )
  const handleSelect = useCallback(
    (visualId: string, row: Record<string, unknown>) => {
      const dt = drillThrough.find((d) => d.SOURCE_VISUAL_ID === visualId)
      // No configured drill-through → a single click does nothing (underlying
      // data is reached by double-click / the ⋮ menu).
      if (!dt) return
      // Absent/unknown TARGET_TYPE = 'dashboard' (models predating types).
      const rawType = typeof dt.TARGET_TYPE === 'string' ? dt.TARGET_TYPE.toLowerCase() : ''
      const targetType = rawType === 'visual' || rawType === 'self' ? rawType : 'dashboard'
      const mapped: Record<string, string> = {}
      for (const [srcCol, targetParam] of Object.entries(dt.PARAM_MAPPING || {})) {
        if (!targetParam) continue
        const v = lookupField(row, srcCol)
        if (v != null) mapped[targetParam] = String(v)
      }
      if (targetType === 'self') {
        // Cross-filter: set THIS dashboard's globals from the clicked row and
        // re-run — no new level. The criteria bar reflects the new values so
        // the user can see/clear them.
        if (Object.keys(mapped).length === 0) return
        const next = { ...active.globals, ...mapped }
        setActiveGlobals(() => next)
        void run({ ...active, globals: next }, true)
        return
      }
      if (targetType === 'visual') {
        const targetVisual = typeof dt.TARGET_VISUAL_ID === 'string' ? dt.TARGET_VISUAL_ID : ''
        if (!targetVisual) {
          console.warn('Drill-through entry has TARGET_TYPE visual but no TARGET_VISUAL_ID', dt)
          return
        }
        setLevels((ls) => [
          ...ls,
          { kind: 'drill', target: 'visual', objName: targetVisual, label: targetVisual, globals: mapped },
        ])
        return
      }
      if (!dt.TARGET_DASHBOARD_ID) return
      setLevels((ls) => [
        ...ls,
        { kind: 'drill', target: 'dashboard', objName: dt.TARGET_DASHBOARD_ID, label: dt.TARGET_DASHBOARD_ID, globals: mapped },
      ])
    },
    [drillThrough, active, run, setActiveGlobals, data],
  )

  const criteria = (data?.dashboard.GLOBAL_CRITERIA ?? []) as unknown as DashboardGlobalCriterion[]
  // Map dashboard globals → the shared criteria-form prompt shape.
  const promptVars = useMemo<VisualCriteriaPromptVar[]>(
    () => criteria.map((c) => ({
      name: c.PARAM_NAME,
      label: c.PROMPT_LABEL || c.PARAM_NAME,
      data_type: c.DATA_TYPE,
      operator: c.OPERATOR,
      // default / has_default so the shared form can seed + show the default;
      // options so a dashboard global can render as a <select> picklist.
      default: c.DEFAULT_VALUE,
      has_default: c.DEFAULT_VALUE != null && String(c.DEFAULT_VALUE).trim() !== '',
      // Mandatory: shown with a *, refused blank on Apply (the server checks too).
      required: c.mandatory === true || String(c.mandatory ?? '').toLowerCase() === 'true',
      options: c.options,
      lookup: c.lookup,
      lookup_filter: c.lookup_filter,
      lookup_filter_rules: c.lookup_filter_rules,
      lookup_sql: c.lookup_sql,
      lookup_title: c.lookup_title,
      lookup_sql_data_col: c.lookup_sql_data_col,
      has_item_change: !!c.item_change,
    })),
    [criteria],
  )
  // Apply: a blank required criterion is said at once rather than asked of the
  // server (which would refuse it the same way).
  const applyCriteria = () => {
    const local = requiredIssues(promptVars, active.globals)
    if (local.length) {
      setIssues(local)
      setFiltersCollapsed(false)
      return
    }
    void run(active, true)
  }
  // Transport for global-criteria F2 lookups + item_change (previewing the
  // unsaved dashboard model).
  const criteriaRuntime = useMemo(
    () => makeVisualCriteriaRuntime({
      project, kind: 'dashboard', obj_name: objectName, inline_model: model,
      notify: { success: toast.success, error: toast.error, info: (m) => toast(m) },
    }),
    [project, objectName, model],
  )
  // Per-tile header visibility, read from the model (absent = shown). Read here
  // rather than from the server-resolved tile so it works in the preview AND the
  // deployed runtime without a backend round-trip. Drilled-into dashboards use a
  // different model, so their tiles fall back to "shown".
  const showHeaderById = useMemo(() => {
    const m = new Map<string, boolean>()
    for (const v of model?.DASHBOARD?.VISUALS || []) {
      m.set(v.VISUAL_ID, v.SHOW_HEADER !== false)
    }
    return m
  }, [model])
  // One in-memory model per data source: the tiles whose plan is supported were
  // NOT queried — they fold their dataset out of these, so a quick filter is a
  // local recompute instead of a dashboard round trip.
  const { models, loading: modelsLoading } = useVisualModels(data?.models)

  // What a tile did with the active chips. A visual tile reports itself (via
  // <InsightVisual onFilterResult>); the two cases a visual can't report — a
  // report tile (its own Jasper query) and a tile that failed to load — are
  // stated here, so the reach count and the tile notices are never optimistic.
  const statusForTile = (tile: DashboardDataResult['tiles'][number]): TileQuickFilterStatus | undefined => {
    if (!chips.length) return undefined
    const allSkipped = (reason: string) => ({
      applied: [],
      skipped: chips.map((c) => ({ column: c.column, reason })),
    })
    if (String(tile.TILE_KIND || '').toLowerCase() === 'report') return allSkipped(REASON_REPORT_TILE)
    if (tile.error) return allSkipped(REASON_TILE_ERROR)
    return tileFilterResults[tile.VISUAL_ID]
  }

  // What this level is, for the header: the base dashboard reads the model in
  // hand (so the designer preview shows unsaved edits); a drilled dashboard
  // reads its own payload; a drilled single visual is named by its level.
  const isBase = active.kind === 'base'
  const headerTitle = isVisualLevel
    ? active.label
    : isBase
      ? (model.DASHBOARD.DASHBOARD_NAME || objectName)
      : (data?.dashboard.DASHBOARD_NAME || active.label)
  // Inside the Run shell its screen title already names the base dashboard.
  const showTitle = useDashScreenShowsTitle(objectName, 'D', { overlay, drilled: !isBase })
  const headerDescription = isVisualLevel
    ? ''
    : isBase ? model.DASHBOARD.DASHBOARD_DESCR : data?.dashboard.DASHBOARD_DESCR
  // The colour this level wears — 'default' = the application's colour.
  const { ref: accentRef, style: accentStyle } = useDashboardAccent(
    isBase ? model.DASHBOARD.THEME_COLOR : data?.dashboard.THEME_COLOR,
    appColor,
  )
  // What a click on a drill-source tile does, so its header can say so.
  const drillKindById = useMemo(() => {
    const m = new Map<string, 'filter' | 'drill'>()
    for (const d of (data?.dashboard.DRILL_THROUGH ?? []) as DashboardDrillThrough[]) {
      m.set(d.SOURCE_VISUAL_ID, String(d.TARGET_TYPE || '').toLowerCase() === 'self' ? 'filter' : 'drill')
    }
    return m
  }, [data])
  const hasVisibleCriteria = promptVars.some((pv) => !pv.hidden)
  // A drilled single visual: the values it was opened with (from the row).
  const drillNote = isVisualLevel && Object.keys(active.globals).length
    ? Object.entries(active.globals).map(([k, v]) => `${k} = ${v}`).join(' · ')
    : undefined

  return (
    <div
      ref={accentRef}
      {...tw('screen', objectName, {
        kind: 'D', state: loading ? 'loading' : error ? 'error' : data ? 'ready' : 'idle',
        tiles: data?.tiles?.length,
      })}
      style={{ ...accentStyle, ...dashScreenPageStyle, padding: screenMargin, minHeight: '100%' }}
    >
      {/* Drill-through trail (only once we've drilled at least once). */}
      {levels.length > 1 && (
        <DashboardDrillTrail
          labels={levels.map((l, i) => (i === 0 ? (model.DASHBOARD.DASHBOARD_NAME || l.label) : l.label))}
          onBack={() => setLevels((ls) => ls.slice(0, -1))}
          onGo={(i) => setLevels((ls) => ls.slice(0, i + 1))}
          note={drillNote}
        />
      )}

      <DashboardHeader
        title={showTitle ? headerTitle : null}
        description={headerDescription}
        updatedAt={updatedAt}
        refreshSeconds={isVisualLevel ? 0 : refreshSeconds}
        tileCount={!isVisualLevel && data ? data.tiles.length : undefined}
        actions={(
          <>
            {/* Quick filter is a MODE — entered here, never armed silently. */}
            {!isVisualLevel && (
              <QuickFilterToggle
                large
                active={quickMode}
                count={chips.length}
                onToggle={() => setQuickMode((m) => !m)}
              />
            )}
            {/* No filters to Apply: the run is still one click away. */}
            {!isVisualLevel && !hasVisibleCriteria && (
              <DashboardIconButton label={loading ? 'Running…' : 'Refresh'} onClick={() => void run(active, true)}>
                <span {...tw('action', 'apply')} style={{ display: 'inline-flex' }}>
                  {loading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={16} />}
                </span>
              </DashboardIconButton>
            )}
            {hostActions}
          </>
        )}
      />

      {/* Global criteria (dashboard levels only — a drilled-into single visual
          carries its params from the clicked row). The shared CriteriaBar in its
          card look: chips of the applied values, the form when opened, Apply.
          Sticky above any tile's own sticky header (a pivot's thead, z 2). */}
      {!isVisualLevel && hasVisibleCriteria && (
        <DashboardFilterCard>
          <CriteriaBar
            look="card"
            project={project}
            promptVars={promptVars}
            values={active.globals}
            onChange={(name, val) => setActiveGlobals((v) => ({ ...v, [name]: val }))}
            onSubmit={applyCriteria}
            runtime={criteriaRuntime}
            collapsed={filtersCollapsed}
            onToggleCollapsed={() => setFiltersCollapsed((c) => !c)}
            issues={issues}
            actions={(
              <button
                type="button"
                onClick={applyCriteria}
                disabled={loading}
                {...tw('action', 'apply')}
                style={dashApplyButtonStyle(loading)}
              >
                <Play size={14} /> {loading ? 'Running…' : 'Apply'}
              </button>
            )}
          />
        </DashboardFilterCard>
      )}
      {!isVisualLevel && (quickMode || chips.length > 0) && (
        <DashboardQuickFilterBand>
          <QuickFilterBar
            active={quickMode}
            chips={chips}
            reach={quickFilterReach(data ? data.tiles.map((t) => statusForTile(t)) : [])}
            onRemove={(column, value) => setChips((c) => removeQuickFilterValue(c, column, value))}
            onClearAll={() => setChips([])}
            onExit={() => setQuickMode(false)}
          />
        </DashboardQuickFilterBand>
      )}

      {error && <PageMessage tone="error">{error}</PageMessage>}
      {loading && !data && !visualData && (
        <PageMessage><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</PageMessage>
      )}

      {/* Drilled-into single visual — one card in place of the grid. */}
      {isVisualLevel && visualData && (
        <div style={{
          flex: 1, minHeight: 460, position: 'relative', borderRadius: 12, overflow: 'hidden',
          border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
          boxShadow: '0 1px 2px var(--color-shadow)',
        }}>
          {visualData.needsInput ? (
            <DashboardTileMessage tone="muted">
              This visual needs input: {(visualData.missingParams || []).join(', ') || 'required parameters missing'}.
            </DashboardTileMessage>
          ) : visualData.jsonData && visualData.visualLayout ? (
            <div style={{ position: 'absolute', inset: 8 }}>
              <InsightVisual
                jsonData={visualData.jsonData}
                visualLayout={visualData.visualLayout}
                optionsArr={visualData.optionsArr}
                menuHeader
                style={{ height: '100%' }}
              />
            </div>
          ) : (
            <DashboardTileMessage tone="muted">No data.</DashboardTileMessage>
          )}
        </div>
      )}

      {data && (
        <div style={{
          // FIXED row height (stretch:false) + align to the top: tiles keep the
          // exact proportions the designer laid out; the page scrolls when the
          // dashboard is taller than the viewport. The negative margin cancels
          // the tiles' half-gap margins so the grid lines up with the header.
          ...dashGridContainerStyle(DASH_SCREEN_ROW_PX, DASH_SCREEN_GAP, DASH_SCREEN_GAP / 2, { stretch: false }),
          alignContent: 'start',
          margin: -DASH_SCREEN_GAP / 2,
        }}>
          {data.tiles.map((tile) => (
            <DashboardTile
              key={tile.VISUAL_ID}
              project={project}
              tile={tile}
              showHeader={isBase
                ? (showHeaderById.get(tile.VISUAL_ID) ?? true)
                : tile.SHOW_HEADER !== false}
              drillKind={drillKindById.get(tile.VISUAL_ID)}
              // Single click → configured drill-through (if any). Double-click a
              // point / the ⋮ menu → the tile's underlying data (client-side).
              onSelect={(row) => handleSelect(tile.VISUAL_ID, row)}
              onShowUnderlying={(payload) => setUnderlying(payload)}
              // Quick filter: the mode arms the click, the expression narrows the
              // data the tile already has.
              quickFilterMode={quickMode}
              quickFilterExpr={quickFilterExpr}
              quickFilterStatus={statusForTile(tile)}
              chips={chips}
              onQuickFilter={(picks, opts) => applyPicks(picks, opts.additive, tile.VISUAL_NAME || tile.VISUAL_ID)}
              onFilterResult={(result) => setTileFilterResults((prev) => ({ ...prev, [tile.VISUAL_ID]: result }))}
              model={tile.plan?.sourceKey ? models.get(tile.plan.sourceKey) : undefined}
              plan={tile.plan}
              modelsLoading={modelsLoading}
            />
          ))}
          {data.tiles.length === 0 && (
            <div style={{ gridColumn: '1 / -1', position: 'relative', minHeight: 160 }}>
              <DashboardTileMessage tone="muted">This dashboard has no tiles.</DashboardTileMessage>
            </div>
          )}
        </div>
      )}

      {/* Underlying-data modal (double-click a tile point / its ⋮ menu). Rows
          are computed client-side by the engine — no backend round-trip. */}
      {underlying && (
        <UnderlyingDataModal
          open
          jsonData={underlying.jsonData}
          visualLayout={underlying.visualLayout}
          filters={underlying.filters}
          onClose={() => setUnderlying(null)}
        />
      )}
    </div>
  )
}

// One dashboard tile on the shared frame: a title bar (name, what a click
// does, the quick-filter badge, and the ⋮ slot the visual engine fills) over
// the visual body.
function DashboardTile({
  project, tile, showHeader, drillKind, onSelect, onShowUnderlying,
  quickFilterMode, quickFilterExpr, quickFilterStatus, chips, onQuickFilter, onFilterResult,
  model, plan, modelsLoading,
}: {
  project: string
  tile: DashboardDataResult['tiles'][number]
  showHeader: boolean
  /** Set when the tile is a drill-through source: 'filter' = this dashboard
   *  ('self'), 'drill' = another dashboard / visual. */
  drillKind?: 'filter' | 'drill'
  onSelect?: (row: Record<string, unknown>) => void
  onShowUnderlying?: (payload: UnderlyingPayload) => void
  quickFilterMode?: boolean
  quickFilterExpr?: FilterExpression | null
  quickFilterStatus?: TileQuickFilterStatus
  chips?: QuickFilterChip[]
  onQuickFilter?: (picks: QuickFilterPick[], opts: { additive: boolean }) => void
  onFilterResult?: (result: FilterResult) => void
  model?: VisualModel
  plan?: VisualPlan
  modelsLoading?: boolean
}) {
  // What this tile could NOT do with the active chips — shown on the tile so
  // an unfiltered tile is obvious rather than misleading.
  const indication = tileQuickFilterIndication(quickFilterStatus, chips ?? [])
  const liveReason = plan && !plan.supported
    ? (MODEL_FALLBACK_REASON_TEXT[plan.reason || ''] || 'it cannot be served from the shared dataset')
    : null
  // Test hook: one tile = one visual; `visual_has_data` reads state/rows. A
  // model-backed tile folds its rows client-side, so its count is not known here.
  const twRows = tile.jsonData ? Math.max(0, tile.jsonData.length - 1) : undefined
  const twState = tile.error ? 'error'
    : tile.TILE_KIND === 'report' ? 'ready'
      : tile.modelBacked && !model ? (modelsLoading ? 'loading' : 'error')
        : twRows === undefined ? (tile.modelBacked ? 'ready' : 'empty')
          : twRows > 0 ? 'ready' : 'empty'
  // While the mode is armed every tile reads as clickable; a tile that
  // couldn't honour the filters is outlined in the warning tone instead.
  const tone = indication && indication.state === 'none' ? 'warn' : quickFilterMode ? 'quick' : 'normal'
  return (
    <DashboardTileFrame
      attrs={tw('block', `tile:${tile.VISUAL_ID || tile.REPORT_OBJ || ''}`, {
        visual: tile.VISUAL_ID, state: twState, rows: twRows,
      })}
      placement={dashTileStyle(tile.POSITION, DASH_SCREEN_GAP)}
      title={tile.VISUAL_NAME || tile.VISUAL_ID}
      showHeader={showHeader}
      tone={tone}
      headerExtras={(
        <>
          {drillKind && !quickFilterMode && <DashboardDrillHint kind={drillKind} />}
          {/* Whether the active quick filter reached this tile (and, either
              way, whether it was queried live rather than folded from the
              shared model). Hovering explains both. */}
          <TileFilterBadge indication={indication} liveReason={liveReason} />
        </>
      )}
      // A header-less tile has nowhere to dock the badge, but it needs the
      // indication just as much — an unmarked tile reads as "this respects
      // your filter".
      floating={(indication || liveReason)
        ? <TileFilterBadge indication={indication} liveReason={liveReason} compact />
        : undefined}
    >
      {(menuHost) => (
        tile.error ? (
          <DashboardTileMessage tone="error">{tile.error}</DashboardTileMessage>
        ) : tile.TILE_KIND === 'report' && tile.REPORT_OBJ ? (
          // Report tile — the shared ReportViewer fills the tile with the
          // server-resolved bound params (criteria bindings → globals).
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
            <ReportEmbedBody
              project={project}
              objName={tile.REPORT_OBJ}
              params={tile.reportParams}
            />
          </div>
        ) : tile.modelBacked && !model ? (
          <DashboardTileMessage tone="muted">
            {modelsLoading ? 'Preparing shared dataset…' : 'Shared dataset unavailable.'}
          </DashboardTileMessage>
        ) : (tile.jsonData || tile.modelBacked) && tile.visualLayout ? (
          <InsightVisual
            jsonData={tile.jsonData ?? EMPTY_JSON_DATA}
            truncated={tile.truncated}
            rowLimit={tile.rowLimit}
            rowCeiling={tile.rowCeiling}
            rowLimitAuthored={tile.rowLimitAuthored}
            rowLimitFixed={tile.rowLimitFixed}
            model={model}
            plan={plan}
            visualLayout={tile.visualLayout}
            optionsArr={tile.optionsArr}
            onSelect={onSelect}
            onShowUnderlying={onShowUnderlying}
            quickFilterMode={quickFilterMode}
            onQuickFilter={onQuickFilter}
            filter={quickFilterExpr}
            onFilterResult={onFilterResult}
            // Header on → the ⋮ menu docks into the tile's title bar. Header off
            // → truly full-bleed (no ⋮ strip); underlying-data is still reachable
            // via double-click.
            chromeHost={menuHost}
            style={{ height: '100%' }}
          />
        ) : (
          <DashboardTileMessage tone="muted">No data.</DashboardTileMessage>
        )
      )}
    </DashboardTileFrame>
  )
}

// A page-level status (loading / error) in the flow of the screen.
function PageMessage({ children, tone }: { children: ReactNode; tone?: 'error' | 'muted' }) {
  return (
    <div style={{ position: 'relative', minHeight: 160 }}>
      <DashboardTileMessage tone={tone}>{children}</DashboardTileMessage>
    </div>
  )
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const resp = (e as { response?: { data?: { detail?: unknown; error?: { message?: string } } } }).response
    const detail = resp?.data?.detail
    if (typeof detail === 'string') return detail
    if (resp?.data?.error?.message) return resp.data.error.message
  }
  return e instanceof Error ? e.message : String(e)
}
